import { Asset } from '../../core/types/asset.types';
import { SpineAsset } from '../../core/types/spine.types';
import { useProjectPanelStore } from '../store/projectPanelStore';
import { useSpineImportStore, useSpineRuntimeStore } from '../store/spineStore';
import { readFile } from '../services/FileSystemService';
import { parseSpineJson, parseSpineAtlas } from './spine-loader';

interface SpineDiscoveryResult {
  success: boolean;
  skeletonAsset: Asset | null;
  atlasAsset: Asset | null;
  textureAssets: Asset[];
  errors: string[];
}

/** Find companion .skel, .atlas, .png files in the same folder as droppedAsset */
export function discoverSpineFileSet(droppedAsset: Asset): SpineDiscoveryResult {
  const { assets } = useProjectPanelStore.getState();
  const folderId = droppedAsset.parentId;

  // Get all assets in the same folder
  const siblings = Object.values(assets).filter((a) => a.parentId === folderId);

  // Extract base name: "hero.skel" -> "hero"
  const baseName = droppedAsset.name.replace(/\.[^.]+$/, '');
  const droppedExt = droppedAsset.extension.toLowerCase();

  let skeletonAsset: Asset | null = null;
  let atlasAsset: Asset | null = null;
  const textureAssets: Asset[] = [];
  const errors: string[] = [];

  // Assign the dropped file
  if (droppedExt === '.skel' || droppedExt === '.json') {
    skeletonAsset = droppedAsset;
  } else if (droppedExt === '.atlas') {
    atlasAsset = droppedAsset;
  }

  // Search siblings for companions — prefer exact base name match
  for (const sibling of siblings) {
    if (sibling.id === droppedAsset.id) continue;
    const sibExt = sibling.extension.toLowerCase();
    const sibBase = sibling.name.replace(/\.[^.]+$/, '');

    if (
      !skeletonAsset &&
      (sibExt === '.skel' || sibExt === '.json') &&
      sibBase === baseName
    ) {
      skeletonAsset = sibling;
    }
    if (!atlasAsset && sibExt === '.atlas' && sibBase === baseName) {
      atlasAsset = sibling;
    }
    if (
      ['.png', '.jpg', '.jpeg', '.webp'].includes(sibExt) &&
      sibBase.startsWith(baseName)
    ) {
      textureAssets.push(sibling);
    }
  }

  // Fallback: if only ONE .atlas/.skel exists in folder, use it
  if (!atlasAsset) {
    const atlasFiles = siblings.filter(
      (a) => a.extension.toLowerCase() === '.atlas'
    );
    if (atlasFiles.length === 1) atlasAsset = atlasFiles[0];
  }
  if (!skeletonAsset) {
    const skelFiles = siblings.filter((a) =>
      ['.skel', '.json'].includes(a.extension.toLowerCase())
    );
    if (skelFiles.length === 1) skeletonAsset = skelFiles[0];
  }

  // Validate
  if (!skeletonAsset) errors.push('No skeleton file (.skel/.json) found in folder');
  if (!atlasAsset) errors.push('No atlas file (.atlas) found in folder');
  if (textureAssets.length === 0)
    errors.push('No texture files (.png) found in folder');

  return {
    success: errors.length === 0,
    skeletonAsset,
    atlasAsset,
    textureAssets,
    errors,
  };
}

/** Discover files, parse metadata, register SpineAsset in store, return asset ID */
export async function discoverAndRegisterSpineAsset(
  droppedAsset: Asset
): Promise<{ assetId: string; error?: string }> {
  const discovery = discoverSpineFileSet(droppedAsset);

  if (!discovery.success || !discovery.skeletonAsset || !discovery.atlasAsset) {
    return { assetId: '', error: discovery.errors.join('. ') };
  }

  return registerSpineFromAssets(
    discovery.skeletonAsset,
    discovery.atlasAsset,
    discovery.textureAssets
  );
}

/** Find texture assets co-located with the atlas file */
function discoverTextures(atlasAsset: Asset, skelAsset?: Asset | null): Asset[] {
  const { assets } = useProjectPanelStore.getState();
  const folderId = atlasAsset.parentId;
  const siblings = Object.values(assets).filter((a) => a.parentId === folderId);
  const baseName = atlasAsset.name.replace(/\.[^.]+$/, '');
  const skelBase = skelAsset
    ? skelAsset.name.replace(/\.[^.]+$/, '')
    : baseName;

  // Collect textures matching base name of either file
  return siblings.filter((a) => {
    const ext = a.extension.toLowerCase();
    if (!['.png', '.jpg', '.jpeg', '.webp'].includes(ext)) return false;
    const sibBase = a.name.replace(/\.[^.]+$/, '');
    return sibBase.startsWith(baseName) || sibBase.startsWith(skelBase);
  });
}

/** Register a spine asset from explicit skeleton + atlas assets */
export async function registerSpineFromAssets(
  skeletonAsset: Asset,
  atlasAsset: Asset,
  textureAssets?: Asset[]
): Promise<{ assetId: string; error?: string }> {
  const { importedAssets } = useSpineImportStore.getState();
  const { loadedAssets } = useSpineRuntimeStore.getState();

  // Deduplication by skeleton path
  const skelPath = skeletonAsset.path;
  const existing =
    importedAssets.find((a) => a.skeletonPath === skelPath) ||
    Object.values(loadedAssets).find((a) => a.skeletonPath === skelPath);
  if (existing) {
    return { assetId: existing.id };
  }

  // Auto-discover textures if not provided
  let textures = textureAssets && textureAssets.length > 0
    ? textureAssets
    : discoverTextures(atlasAsset, skeletonAsset);

  // Parse atlas to refine texture list by page names
  let parsedAtlas: ReturnType<typeof parseSpineAtlas> | null = null;
  try {
    const atlasText = await readFile(atlasAsset.path);
    parsedAtlas = parseSpineAtlas(atlasText);
  } catch {
    /* atlas parsing optional */
  }

  if (parsedAtlas && parsedAtlas.pages.length > 0) {
    const { assets: allAssets } = useProjectPanelStore.getState();
    const folderId = atlasAsset.parentId;
    const folderAssets = Object.values(allAssets).filter(
      (a) => a.parentId === folderId
    );
    const pageNames = parsedAtlas.pages.map((p) => p.name);

    const refinedTextures = pageNames
      .map((name) => folderAssets.find((a) => a.name === name))
      .filter(Boolean) as Asset[];

    if (refinedTextures.length > 0) {
      textures = refinedTextures;
    }
  }

  // Parse skeleton metadata (if JSON)
  let skeletonMeta: Partial<SpineAsset> = {};
  if (skeletonAsset.extension.toLowerCase() === '.json') {
    try {
      const skelJson = await readFile(skeletonAsset.path);
      skeletonMeta = parseSpineJson(skelJson);
    } catch {
      /* skeleton parsing optional */
    }
  }

  // Register in store
  const { registerAssetFromProject } = useSpineRuntimeStore.getState();
  const baseName = skeletonAsset.name.replace(/\.[^.]+$/, '');

  const spineAsset = registerAssetFromProject(
    baseName,
    skeletonAsset.path,
    atlasAsset.path,
    textures.map((t) => t.path),
    skeletonMeta
  );

  return { assetId: spineAsset.id };
}
